import _ from 'lodash';
import {connect} from 'react-redux';

import {UploadedAttachmentModel} from '../../../../../libs/shared/core/src/models/api/attachmentModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {getDefaultCellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {PanelTypesEnum, ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {isLinkedConversationActivity} from '../../../../models/api/activityModel';
import {TeammatesFoldersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {AppState} from '../../../../store/appState';
import {saveCommentAsyncAction} from '../../../../store/data/comments/saveCommentActions';
import {sendCommentAsyncAction} from '../../../../store/data/comments/sendCommentAction';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {getActivities, getComments, getConversations} from '../../../../store/data/dataSelectors';
import {getMentionableAliases} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindOnlineStatus} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {getFindTeammate, getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {normalInboxesSelection} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {getBuildDefaultInboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {getPanels, makeGetIsPanelPopout} from '../../../../store/panels/panelsSelectors';
import {navigateViewerAsyncAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {getEmojiValidationMap} from '../../../../store/stateless/emojiSelectors';
import {SendCommentOptions} from '../commentComposer/commentComposer';
import {InternalComposer} from './internalComposer';

/*
 * Props.
 */

interface InternalComposerContainerProps {
  panelId: ViewerPanelId;
  commentUid: string;
}

/*
 * Selectors.
 */

function getCommentUid(_state: AppState, props: InternalComposerContainerProps) {
  return props.commentUid;
}

function getPanelId(_state: AppState, props: InternalComposerContainerProps) {
  return props.panelId;
}

function makeGetPanel() {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    if (panel?.type === PanelTypesEnum.VIEWER) {
      return panel;
    }

    return undefined;
  });
}

function makeGetComment() {
  return createSelector([getComments, getCommentUid], (comments, commentUid) => {
    const comment = comments[commentUid];
    if (!comment) {
      throw new Error('A comment could not be found for this ID.');
    }

    return comment;
  });
}

function makeGetTeammates(getComment: ReturnType<typeof makeGetComment>) {
  return wrapInShallowSelector(
    createSelector([getFindTeammate, getComment], (findTeammate, comment) =>
      _(comment.teammateIds)
        .map((id) => findTeammate(id))
        .compact()
        .value(),
    ),
  );
}

function makeGetConversation(getComment: ReturnType<typeof makeGetComment>) {
  return createSelector(
    [getComment, getConversations],
    // For new discussions, `conversationsId` will be `0`, and a conversation will not be found.
    // For new linked discussions, a conversations will be have already been persisted and present in the store.
    (comment, conversations) => conversations[comment.conversationId] || null,
  );
}

function makeGetOriginalLinkedDiscussionActivity() {
  return createSelector([makeGetPanel(), getActivities], (panel, activities) => {
    if (!panel) {
      return null;
    }
    const {linkedActivityId} = panel;
    if (linkedActivityId === undefined) {
      return null;
    }
    const activity = activities[linkedActivityId];
    return isLinkedConversationActivity(activity) ? activity : null;
  });
}

function makeGetOriginalLinkedConversationInboxId() {
  const getOriginalLinkedDiscussionActivity = makeGetOriginalLinkedDiscussionActivity();
  return createSelector(
    [getOriginalLinkedDiscussionActivity, getConversations],
    (activity, conversations) => {
      if (!activity?.linkedConversationId) {
        return null;
      }
      const linkedConversation = conversations[activity.linkedConversationId];
      return linkedConversation?.inboxIds[0] || null;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InternalComposerContainerProps>()(() => {
  const getComment = makeGetComment();
  const getCommentTeammates = makeGetTeammates(getComment);
  const getConversation = makeGetConversation(getComment);

  return {
    company: getCompany,
    user: getUser,
    comment: getComment,
    buildDefaultParameters: getBuildDefaultInboxesParameters,
    mentionableAliases: getMentionableAliases,
    teammates: getCommentTeammates,
    findOnlineStatus: getFindOnlineStatus,
    emojiValidationMap: getEmojiValidationMap,
    isComposerPopout: makeGetIsPanelPopout(getPanelId),
    conversation: getConversation,
    originalLinkedConversationActivity: makeGetOriginalLinkedDiscussionActivity(),
    originalLinkedConversationInboxId: makeGetOriginalLinkedConversationInboxId(),
    cellsContext: getDefaultCellsContext,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  saveCommentAsync: saveCommentAsyncAction,
  sendCommentAsync: sendCommentAsyncAction,
  navigateViewerAsync: navigateViewerAsyncAction,
  dataUpdated: dataUpdatedAction,
}));

/*
 * Event handlers.
 */

export const InternalComposerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {panelId} = ownProps;
    const {comment, buildDefaultParameters, originalLinkedConversationInboxId, cellsContext} = stateProps;

    const onTeammatesChange: (arg: ReadonlyArray<TeammateModel>) => void = (newTeammates) => {
      // Write the added teammates to redux.
      dispatchProps.dataUpdated({teammates: newTeammates});

      const newTeammateIds = newTeammates.map((t) => t.id);
      dispatchProps.saveCommentAsync(comment, {panelId, newTeammateIds});
    };

    const onTopicChange: (arg: string) => void = (newTopic) => {
      dispatchProps.saveCommentAsync(comment, {panelId, newTopic});
    };

    const onTextChange: (arg: string) => void = (newText) => {
      dispatchProps.saveCommentAsync(comment, {panelId, newText});
    };

    const addAttachment: (model: UploadedAttachmentModel) => void = (attachment) => {
      dispatchProps.saveCommentAsync(comment, {panelId, addedAttachments: [attachment]});
    };

    const onRemoveAttachmentClick: (arg: string) => void = (attachmentCid) => {
      dispatchProps.saveCommentAsync(comment, {panelId, removedAttachmentCids: [attachmentCid]});
    };

    const sendComment = async (options: SendCommentOptions) => {
      // Wait for any save API requests from the comment composer to settle, and make one more attempt to flush any
      // unsaved changes and obtain a conversation ID from the server.
      const saveResult = await dispatchProps.saveCommentAsync(comment, {
        panelId,
        onlyIfDirty: true,
      });

      // If the comment is in a failed state, cancel the send operation.
      if (!saveResult) {
        throw new Error('The comment to send was not properly saved.');
      }

      // Keep track of validation errors.
      let didThrow = false;
      function notifyOnThrow() {
        didThrow = true;
      }

      const inboxId = options.inboxId || originalLinkedConversationInboxId || undefined;

      // Start the send process.
      const sendPromise = dispatchProps.sendCommentAsync({
        cellsContext,
        conversationId: saveResult.conversationId,
        commentUid: saveResult.commentUid,
        notifyOnThrow,
        inboxId,
        ignoreWarnings: options.ignoreWarnings,
        sendAction: options.sendAction,
      });

      // If validation errors were found, stop here.
      if (didThrow) {
        return sendPromise;
      }

      // Otherwise, switch to the conversation.
      // Note: running this navigation with local front client against local
      // front server will be glitchy. See:
      // https://github.com/frontapp/front-client/pull/18893#discussion_r1829545061
      const parameters = buildDefaultParameters({
        folder: TeammatesFoldersEnum.DISCUSSIONS,
        selection: normalInboxesSelection([saveResult.conversationId]),
      });
      dispatchProps.navigateViewerAsync(panelId, parameters, {isAlreadyFullyLoaded: true});

      return sendPromise;
    };

    return {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      onTeammatesChange,
      onTopicChange,
      onTextChange,
      addAttachment,
      onRemoveAttachmentClick,
      sendComment,
      editLastComment: () => {},
      endCommentEdit: () => {},
    };
  },
)(InternalComposer);
